/**
 * Модели нативной авторизации (бинарь core, база `env.coreApiBase`).
 *
 * Типы описаны руками, а не сгенерированы: core не отдаёт свою спеку
 * (`/swagger/doc.json` там 404). Формы сверены с живыми ответами дева и с
 * ответом бэка по спеке v2-auth-tasks-day1-backend-spec.md от 23.08.2026.
 */

/** Роли тенанта. `admin` и `tenant_owner` видят все задачи, остальные — свои. */
export type NativeRole = 'tenant_owner' | 'admin' | 'manager' | 'employee';

export const ELEVATED_ROLES: NativeRole[] = ['tenant_owner', 'admin'];

/** Пользователь нативной сессии — ответ `GET /auth/me` и поле `user` токенов. */
export type NativeUser = {
  id: string;
  tenantId: string;
  email: string;
  displayName: string;
  status: string;
  /**
   * Связка с Bitrix. У пользователя, заведённого через `/auth/register`, её
   * нет и не будет: бэк отказался связывать нативные аккаунты с Bitrix
   * (P1-1 в ответе от 23.08.2026). Держим поле, потому что модель бэка его
   * отдаёт, но полагаться на него нельзя.
   */
  bitrixUserId?: number;
  permissionsVersion?: number;
  /** Появилось по нашему запросу P0-4. У старых сборок бэка поля нет. */
  roles?: NativeRole[];
  createdAt?: string;
  updatedAt?: string;
};

/**
 * Успешный вход: `/auth/register`, `/auth/login` без 2FA, `/auth/mfa/verify`,
 * `/auth/google/select-tenant`.
 */
export type NativeTokenResponse = {
  access_token: string;
  refresh_token: string;
  user: NativeUser;
};

export type LoginPayload = {
  tenant_slug: string;
  email: string;
  password: string;
};

export type RegisterPayload = LoginPayload & {
  first_name: string;
  last_name: string;
};

/*
 * Вход в несколько шагов, Google и 2FA — по инструкции бэка frontend-auth-guide
 * (15.09.2026). Живьём формы не сверены: в день реализации дев отвечал 502.
 */

/**
 * Способ, которым пользователь уже входил в компанию. Это подсказка, а не
 * запрет: кнопка Google видна всегда, привязка происходит при первом входе.
 */
export type AuthMethod = 'password' | 'google';

/** Компания, где заведена почта: `tenants` в `/auth/identify` и во входе через Google. */
export type AuthTenant = {
  tenant_id: string;
  slug: string;
  name: string;
  methods: AuthMethod[];
};

/** Ответ `POST /auth/identify`. */
export type IdentifyResponse =
  | { status: 'not_found' }
  | { status: 'found'; tenants: AuthTenant[] };

/** Ответ `/auth/login` при включённой 2FA: токенов нет, mfa_token живёт 5 минут. */
export type MfaChallenge = {
  mfa_required: true;
  mfa_token: string;
};

/** Ответ `POST /auth/login`: либо сразу сессия, либо запрос второго фактора. */
export type LoginResponse = NativeTokenResponse | MfaChallenge;

/** Ответ `GET /auth/google/callback` — все три варианта приходят с кодом 200. */
export type GoogleCallbackResponse =
  | ({ status: 'logged_in' } & NativeTokenResponse)
  | { status: 'select_tenant'; select_token: string; tenants: AuthTenant[] }
  | { status: 'not_found' };

/** Ответ `POST /auth/mfa/setup`: секрет и ссылка для QR-кода. */
export type MfaSetupResponse = {
  secret: string;
  otpauthUrl: string;
};

/** Элемент справочника `GET /v2/users`. Это не тот же тип, что `NativeUser`. */
export type NativeUserListItem = {
  id: string;
  email: string;
  displayName: string;
  status: string;
  /** У пользователей, приехавших из Bitrix, ссылка на фото в корпоративном портале. */
  avatarUrl?: string | null;
  firstName?: string;
  lastName?: string;
  workPosition?: string;
  bitrixUserId?: number;
};

/** Навык пользователя — свободная пара «название → значение». */
export type UserSkill = {
  name: string;
  value: string;
};

/** Тело `PATCH /v2/users/{id}`. Все поля необязательные, шлём только изменённые. */
export type NativeUserUpdate = {
  firstName?: string;
  lastName?: string;
  workPosition?: string;
  avatarUrl?: string;
  bitrixUserId?: number;
};

/** `admin` или `tenant_owner` — видит все задачи тенанта, а не только свои. */
export function hasElevatedRole(roles: NativeRole[] | undefined): boolean {
  return (roles ?? []).some((role) => ELEVATED_ROLES.includes(role));
}

/** Логин не выдал сессию, а запросил второй фактор. */
export function isMfaChallenge(
  response: LoginResponse
): response is MfaChallenge {
  return 'mfa_required' in response && response.mfa_required;
}
